// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

export const KEY_CODES = {
  TAB: 9,
  ENTER: 13,
  ESCAPE: 27,
  ARROW_UP: 38,
  ARROW_DOWN: 40
};

export const VIDEO_INPUT_QUALITY = {
  '360p': '360p (nHD) @ 15 fps (600 Kbps max)',
  '540p': '540p (qHD) @ 15 fps (1.4 Mbps max)',
  '720p': '720p (HD) @ 15 fps (1.4 Mbps max)'
};

export const LANGUAGE = 'ja';

export enum AppTypes {
  Kaiin = 'kaiin',
  Public = 'public'
}

export enum ItemArtPath {
  jr03 = 'rent_store',
  jr04 = 'rent_office',
  jr02 = 'rent_parking',
  jr05 = 'rent_tochi',
  jr06 = 'rent_other',
  jr06Souko = 'rent_souko',
  js15 = 'buy_store',
  js16 = 'buy_office',
  js17 = 'buy_other',
  kr01 = 'chintai',
  ks12 = 'mansion',
  ks22 = 'mansion',
  ks13 = 'kodate',
  ks14 = 'tochi',
  rs20 = 'resort'
}

/** スタッフの最大数 */
export const MEMBER_MAX_LENGTH = 30;

export const Errors = {
  StringTooLong: (displayName: string, maxLength: number) =>
    `${displayName}の入力文字は${maxLength}文字以内にしてください。`,
  InvalidMessage: (displayName: string) => `${displayName}が未入力です。`,
  InvalidCharacter: (displayName: string) =>
    `${displayName}に機種依存もじは使用できません。`,
  InviteFailed: 'ユーザーの招待が失敗しました。',
  InvalidThanksMessage: '自動応答メッセージが未入力です。',
  InvalidAutoResponse: '営業時間外応答メッセージが未入力です。',
  WrongEmail: 'メールアドレスを正しく入力してください。',
  ImageChanged:
    'まだ画像を保存していません。本当に別の画面に移動してもよろしいですか？\n保存せずに移動すると、切り抜いた画像が失われる可能性があります。\n保存せずに続行しますか？',
  FieldChanged:
    'まだ変更内容を保存していません。本当に別の画面に移動してもよろしいですか？\n保存せずに移動すると、入力したテキストが失われる可能性があります。\n保存せずに続行しますか？'
};

/** エラー内容 */
export const ErrorMessageConstant = {
  ER001: 'メールアドレスを正しく入力してください。',
  ER002: '認証コードが正しくありません。正しい認証コードを入力してください。',
  ER003: 'アカウントが利用停止中です。',
  ER005: 'スタッフの登録数が上限に達しています。',
  ER006: '既に作成済みのアカウントです。',
  ER007: 'このメールアドレスは仮登録中です。本登録が完了していません。',
  ER008:
    'パスワード再設定の試行回数が制限を超えました。15分後に再度設定してください。',
  ER010:
    '申し訳ありませんが、入力されたメールアドレスに問題があるようです。\nお手数ですが、当社営業担当者か、カスタマーサポートにお問い合わせください。\nその際、「入力されたメールアドレス、入力された日時」をお伝えいただきますようよろしくお願いいたします。',
  ER011:
    '初期パスワードの有効期限が切れました。\n再度、スタッフID追加のお手続きをお願いいたします。',
  ER012:
    'セッションの有効期限が切れました。再度、メールに記載のURLからログインしてください。',
  ER013: 'メールアドレス(ログインIDになります)とスタッフ名を入力してください。',
  ER014: 'メールアドレス(ログインIDになります)を入力してください。',
  ER015: 'スタッフ名を入力してください。',
  ER016:
    'ログインが正常に処理されませんでした。\nお手数ですが、加盟店専用サイトに戻り、もう一度ログインをお願いいたします。',
  ER017: '無効なURLです。',
  ER018:
    'URLの有効期限が過ぎました。\n恐れ入りますが、管理会社さまにお問合せください。',
  ER023:
    '招待メールが届いています。招待用URLを使用してログインしてください。\n招待メールが届いていない、もしくは招待が期限切れの場合は、管理会社さまにご連絡をお願いします。',
  ER024:
    'メールアドレス変更の試行回数が制限を超えました。15分後に再度設定してください。',
  ER025:
    '既に登録されているメールアドレスです。他のメールアドレスをご利用ください。',
  ER026: '変更前と同じメールアドレスです。他のメールアドレスをご利用ください。',
  ER027:
    '確認コードの試行回数が制限を超えました。15分後に再度設定してください。'
} as const;

/** パスワードの正規表現 */
export const passworPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\@\$\!\#\%\+\-])[A-Za-z\d\@\$\!\#\%\+\-]{8,12}$/;

/** パスワードのエラー */
export const commonPasswordError =
  'パスワードは8文字~12文字で大文字、小文字、数字、記号(!@#$%+-) をそれぞれ少なくとも1文字使用してください';

/** LINE問い合わせ・コネクトの利用状況 */
export enum AccountStatus {
  /** 停止 */
  Ban = 'ban',
  /** 退会 / 利用開始前 */
  Withdrawal = 'withdrawal',
  /** 使用中 */
  InUse = 'inuse'
}

/** ユーザーの種類 */
export enum ConnectUserType {
  /** 管理会社・不動産会社 */
  Estate = 'estate'
}

/** メッセージ入力の最大文字列数 */
export const MESSAGE_INPUT_MAX_LENGTH = 1000;

/** チャット一覧の表示状態 */
export enum ChatListDisplayStatus {
  Default,
  Search,
  Result
}

/** iosのCanvasの面積が最大限度 */
export const IOS_CANVAS_SIZE_LIMIT = 16777216;
